/**
 * Kid-friendly copy explaining how each sticker is earned, shown in the
 * tap-to-open card on the My Stickers screen (ExplainableSticker.tsx).
 *
 * Pure + side-effect-free so it's cheap to unit-test the wording rules.
 * Language targets a 6-year-old reader: short, concrete, one sentence,
 * no jargon. Earned firsts read in the past tense ("you did X");
 * per-session stickers (earnable again and again) read as a goal
 * ("do X"); locked streak / total silhouettes stay deliberately vague so
 * the milestone behind them remains a surprise (spec §8).
 */
import type { StickerCategory } from "@/components/StickerEmblem";

export interface StickerExplanation {
  /** Heading on the card — usually the sticker's name. */
  title: string;
  /** One short, age-6 sentence: how the sticker is earned. */
  body: string;
}

/** Locked streak / total silhouettes — keep the milestone a surprise. */
const SURPRISE: StickerExplanation = {
  title: "A secret sticker!",
  body: "Keep practising to unlock this surprise.",
};

/** Firsts are only ever shown once earned — past tense, celebratory. */
const FIRST_BODIES: Record<string, string> = {
  first_practice: "You finished your very first practice!",
  first_win: "You spelled your very first word right!",
  first_perfect: "You spelled every word right on your first try!",
};

/** Per-session stickers can be earned again and again, so the copy is a
 *  goal ("do this") rather than a one-off "you did this". */
const PER_SESSION_BODIES: Record<string, string> = {
  perfect: "Spell every word right on your first try!",
  wrote_it_out: "Write out all your tricky words three times each.",
  better_than_yesterday: "Get a better score than you did yesterday.",
  comeback: "Spell a word right that you got wrong last time.",
  clean_sweep: "Master every word in this week's list.",
};

/** School-year stickers. Awarded for the year itself, not for anything Eva
 *  does in the app, so the earned copy is a flat congratulation and the
 *  locked copy says plainly what's coming — there's no surprise to keep. */
const SCHOOL_BODIES: Record<string, { earned: string; locked: string }> = {
  year_1_complete: {
    earned: "You finished your whole first year at school!",
    locked: "Finish Year 1 to unlock this one.",
  },
  year_2_complete: {
    earned: "You finished your whole second year at school!",
    locked: "Finish Year 2 to unlock this one.",
  },
};

/** Last run of digits in an id, e.g. "streak_7" → 7, "sessions_100" → 100. */
function trailingNumber(id: string): number | null {
  const m = id.match(/(\d+)$/);
  return m ? Number(m[1]) : null;
}

export function explainSticker(props: {
  category: StickerCategory;
  stickerId: string;
  name: string;
  silhouette?: boolean;
  locked?: boolean;
}): StickerExplanation {
  const { category, stickerId, name, silhouette, locked } = props;

  // Unearned streak / total render as nameless silhouettes; never reveal
  // the milestone behind them (spec §8).
  if (silhouette) return SURPRISE;

  switch (category) {
    case "school": {
      const copy = SCHOOL_BODIES[stickerId];
      if (!copy) return { title: name, body: "A school-year sticker!" };
      return { title: name, body: locked ? copy.locked : copy.earned };
    }
    case "first":
      return { title: name, body: FIRST_BODIES[stickerId] ?? "A special first-time sticker!" };
    case "per_session":
      return { title: name, body: PER_SESSION_BODIES[stickerId] ?? "" };
    case "mastery": {
      // Achievement name is "Mastered: <word>"; the word is already on the art.
      const word = name.replace(/^Mastered:\s*/i, "");
      return {
        title: `You mastered “${word}”!`,
        body: "You spelled it right five times in a row.",
      };
    }
    case "streak": {
      const n = trailingNumber(stickerId);
      return {
        title: name,
        body: n ? `You practised ${n} days in a row!` : "You kept a big practice streak going!",
      };
    }
    case "total": {
      const n = trailingNumber(stickerId);
      if (stickerId.startsWith("mastered_")) {
        return {
          title: name,
          body: n ? `You mastered ${n} different words!` : "You mastered lots of words!",
        };
      }
      return {
        title: name,
        body: n ? `You have practised ${n} times!` : "You have practised lots of times!",
      };
    }
    default:
      return { title: name, body: "" };
  }
}
